/**
 * Stage Door Access - Main Application
 */

import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CheckoutProvider } from './context/CheckoutContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { useMarker } from './hooks/useMarker';
import { usePageTracking } from './hooks/usePageTracking';

// Auth Pages
import {
  LoginPage,
  RegisterPage,
  RegisterSuccessPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  ActivatePage,
  AdminInvitePage,
} from './pages/auth';

// Event Pages
import { EventSearchPage, EventDetailPage } from './pages/events';

// Booking Pages
import { BookingPage } from './pages/booking';

// Checkout Pages
import { CheckoutPage, CheckoutConfirmationPage } from './pages/checkout';

// Inquiry Pages
import { InquiryPage, InquiryStatusPage } from './pages/inquiries';

// Dashboard
import DashboardPage from './pages/dashboard/DashboardPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import ContactsPage from './pages/dashboard/ContactsPage';
import BookingsPage from './pages/dashboard/BookingsPage';
import BookingDetailPage from './pages/dashboard/BookingDetailPage';
import InquiriesPage from './pages/dashboard/InquiriesPage';

// Faculty Pages
import {
  FacultyDashboardPage,
  FacultyOnboardingPage,
  FacultyProfilePage,
  FacultySchedulePage,
  FacultyPayoutsPage,
  FacultyInvitationPage,
} from './pages/faculty';

// Admin Pages
import {
  AdminLayout,
  AdminDashboardPage,
  ShowsListPage,
  ShowFormPage,
  TourStopsListPage,
  TourStopFormPage,
  CalendarPage,
  BookingsListPage as AdminBookingsListPage,
  BookingDetailPage as AdminBookingDetailPage,
  BookingFormPage,
  InquiriesListPage,
  InquiryDetailPage,
  FacultyListPage,
  FacultyDetailPage,
  FacultyFormPage,
  CampaignsListPage,
  CampaignFormPage,
  InterestListPage,
  WaitlistPage,
  CouponsListPage,
  CouponFormPage,
  AdminFacultyPayoutsPage,
  SystemSettingsPage,
  EmailCallbackPage,
} from './pages/admin';
import { StudiosListPage, StudioDetailPage } from './pages/admin/studios';

// Home Page
import { HomePage } from './components/home';

/**
 * 404 Page
 */
function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-50">
      <div className="text-center">
        <h1 className="text-6xl font-display text-warm-300 mb-4">404</h1>
        <h2 className="text-2xl mb-4">Page Not Found</h2>
        <p className="text-warm-600 mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a href="/" className="btn-primary">
          Return Home
        </a>
      </div>
    </div>
  );
}

/**
 * MarkerWidget - Initializes Marker.io bug reporting (admins only) and syncs
 * reporter with auth state. Non-admin users never load the widget.
 */
function MarkerWidget() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const { widget } = useMarker(isAdmin);

  useEffect(() => {
    if (!widget) return;

    if (user) {
      widget.setReporter({
        email: user.email,
        fullName: user.email,
      });
    } else {
      widget.clearReporter();
    }
  }, [widget, user]);

  return null;
}

/**
 * SDA-299: logs one page view per route change (allowlisted pages only, server side).
 * Sits inside AuthProvider and the BrowserRouter in main.tsx.
 */
function PageTracker() {
  usePageTracking();
  return null;
}

/**
 * Main App Component
 */
export default function App() {
  return (
    <AuthProvider>
      <MarkerWidget />
      <PageTracker />
      <CheckoutProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<EventSearchPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />

        {/* Auth Routes (public only - redirect if authenticated) */}
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnlyRoute>
              <RegisterPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/register/success"
          element={
            <PublicOnlyRoute>
              <RegisterSuccessPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicOnlyRoute>
              <ForgotPasswordPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <PublicOnlyRoute>
              <ResetPasswordPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/auth/verify"
          element={<VerifyEmailPage />}
        />
        <Route
          path="/activate"
          element={<ActivatePage />}
        />

        {/* Booking Routes - Protected */}
        <Route
          path="/booking/:tourStopId"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <BookingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/confirmation/:bookingId"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <CheckoutConfirmationPage />
            </ProtectedRoute>
          }
        />

        {/* Inquiry Routes - Public (guest submissions allowed) */}
        <Route path="/inquiry/status/:token" element={<InquiryStatusPage />} />
        <Route path="/inquiry/:tourStopId" element={<InquiryPage />} />

        {/* Protected Routes - Studio Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/profile"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/contacts"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <ContactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/bookings"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <BookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/bookings/:bookingId"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <BookingDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/inquiries"
          element={
            <ProtectedRoute allowedRoles={['studio']}>
              <InquiriesPage />
            </ProtectedRoute>
          }
        />

        {/* Faculty Invitation - Public (no auth required) */}
        <Route path="/faculty/invitation/:token" element={<FacultyInvitationPage />} />

        {/* Admin Invitation - Public (token-gated; not linked from anywhere) */}
        <Route path="/admin-invite/:token" element={<AdminInvitePage />} />

        {/* Legacy faculty URL (SDA-160) — old emails sent before SDA-150 still
            link to /faculty/dashboard. Real landing is /faculty. */}
        <Route path="/faculty/dashboard" element={<Navigate to="/faculty" replace />} />

        {/* Legacy studio settings URL (SDA-161/162) — defensive. Backend pending
            action was fixed to point at /dashboard/profile, but any cached or
            client-side stale URL still resolves cleanly. */}
        <Route path="/dashboard/settings" element={<Navigate to="/dashboard/profile" replace />} />

        {/* Faculty Portal Routes - Protected */}
        <Route
          path="/faculty"
          element={
            <ProtectedRoute allowedRoles={['faculty']}>
              <FacultyDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faculty/onboarding"
          element={
            <ProtectedRoute allowedRoles={['faculty']}>
              <FacultyOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faculty/profile"
          element={
            <ProtectedRoute allowedRoles={['faculty']}>
              <FacultyProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faculty/schedule"
          element={
            <ProtectedRoute allowedRoles={['faculty']}>
              <FacultySchedulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faculty/payouts"
          element={
            <ProtectedRoute allowedRoles={['faculty']}>
              <FacultyPayoutsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Login - Redirect to regular login with state */}
        <Route
          path="/admin/login"
          element={
            <Navigate to="/login" state={{ from: { pathname: '/admin' } }} replace />
          }
        />

        {/* Admin Portal Routes - Protected */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<AdminDashboardPage />} />

          {/* Shows Management */}
          <Route path="shows" element={<ShowsListPage />} />
          <Route path="shows/new" element={<ShowFormPage />} />
          <Route path="shows/:id/edit" element={<ShowFormPage />} />

          {/* Tour Stops Management */}
          <Route path="tour-stops" element={<TourStopsListPage />} />
          <Route path="tour-stops/new" element={<TourStopFormPage />} />
          <Route path="tour-stops/:id/edit" element={<TourStopFormPage />} />

          {/* Calendar */}
          <Route path="calendar" element={<CalendarPage />} />

          {/* Bookings Management */}
          <Route path="bookings" element={<AdminBookingsListPage />} />
          <Route path="bookings/new" element={<BookingFormPage />} />
          <Route path="bookings/:id" element={<AdminBookingDetailPage />} />
          <Route path="bookings/:id/edit" element={<BookingFormPage />} />

          {/* Inquiries Management */}
          <Route path="inquiries" element={<InquiriesListPage />} />
          <Route path="inquiries/:id" element={<InquiryDetailPage />} />

          {/* Faculty Management */}
          <Route path="faculty" element={<FacultyListPage />} />
          <Route path="faculty/new" element={<FacultyFormPage />} />
          <Route path="faculty/:id" element={<FacultyDetailPage />} />
          <Route path="faculty/:id/edit" element={<FacultyFormPage />} />

          {/* Studios Management */}
          <Route path="studios" element={<StudiosListPage />} />
          <Route path="studios/:id" element={<StudioDetailPage />} />

          {/* Campaigns */}
          <Route path="campaigns" element={<CampaignsListPage />} />
          <Route path="campaigns/new" element={<CampaignFormPage />} />
          <Route path="campaigns/interest" element={<InterestListPage />} />
          <Route path="campaigns/:id" element={<CampaignFormPage />} />
          <Route path="campaigns/:id/edit" element={<CampaignFormPage />} />

          {/* Waitlist */}
          <Route path="waitlist" element={<WaitlistPage />} />

          {/* Coupons */}
          <Route path="coupons" element={<CouponsListPage />} />
          <Route path="coupons/new" element={<CouponFormPage />} />
          <Route path="coupons/:id/edit" element={<CouponFormPage />} />

          {/* Faculty Payouts */}
          <Route path="payouts" element={<AdminFacultyPayoutsPage />} />

          {/* Settings */}
          <Route path="settings" element={<SystemSettingsPage />} />
          <Route path="settings/email-callback" element={<EmailCallbackPage />} />
        </Route>

        {/* Catch all - 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </CheckoutProvider>
    </AuthProvider>
  );
}
